<template>
  <div
    class="call-analysis-accordion"
    :class="{ 'call-analysis-accordion--flat': isUnansweredCall }"
  >
    <button
      type="button"
      class="accordion-header"
      :class="{ 'is-expanded': isExpanded, 'accordion-header--flat': isUnansweredCall }"
      @click="isUnansweredCall ? undefined : toggleAccordion()"
      :disabled="!callSid"
    >
      <div class="accordion-header-content">
        <div class="accordion-left-section">
          <Icon
            icon="mdi:phone-outline"
            width="20"
            height="20"
            class="phone-icon"
            :class="{ 'phone-icon--muted': isUnansweredCall }"
          />
          <div class="accordion-main-info">
            <div class="accordion-title-row">
              <span class="accordion-title">{{
                isUnansweredCall ? unansweredTitle : "Call has been made"
              }}</span>

              <span
                v-if="callStatus"
                class="accordion-status-badge"
                :class="getStatusClass(callStatus)"
              >
                {{ formatStatus(callStatus) }}
              </span>
              <div v-if="callSid" ref="dispositionAnchorRef" class="disp-chip-wrapper" @click.stop>
                <div
                  role="button"
                  tabindex="0"
                  class="disp-chip"
                  :class="{
                    'disp-chip--set': displayDispositionName,
                    'disp-chip--disabled': isUpdatingDisposition,
                  }"
                  @click.stop="isUpdatingDisposition ? undefined : toggleDispositionDropdown()"
                  @keydown.enter.stop.prevent="
                    isUpdatingDisposition ? undefined : toggleDispositionDropdown()
                  "
                  @keydown.space.stop.prevent="
                    isUpdatingDisposition ? undefined : toggleDispositionDropdown()
                  "
                >
                  <Icon icon="mdi:tag-outline" width="13" height="13" />
                  <span>{{
                    isUpdatingDisposition
                      ? "Updating..."
                      : displayDispositionName || "Set Disposition"
                  }}</span>
                  <Icon icon="mdi:chevron-down" width="13" height="13" />
                </div>
                <Teleport to="body">
                  <div v-if="showDispositionDropdown" class="disp-dropdown" :style="dropdownStyle">
                    <button
                      v-for="disp in callDispositions"
                      :key="disp.id"
                      type="button"
                      class="disp-dropdown__item"
                      :class="{ 'disp-dropdown__item--active': localDispositionId === disp.id }"
                      @click.stop="selectDisposition(disp)"
                    >
                      {{ disp.name }}
                    </button>
                  </div>
                </Teleport>
              </div>
            </div>
            <div v-if="!isUnansweredCall" class="accordion-subtitle">Call Details & Analysis</div>
            <span v-if="performedBy" class="timestamp-performer">
              <Icon icon="mdi:account-outline" width="11" height="11" />
              {{ performedBy }}
            </span>
          </div>
        </div>
        <div class="accordion-right-section">
          <div class="accordion-timestamp">
            <span class="timestamp-date">{{ loggedDate }}</span>
            <span class="timestamp-time">{{ loggedTime }}</span>
          </div>
          <Icon
            v-if="!isUnansweredCall"
            :icon="isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'"
            width="22"
            height="22"
            class="accordion-icon"
          />
        </div>
      </div>
    </button>

    <div v-if="isExpanded && !isUnansweredCall" class="accordion-content">
      <div v-if="joinRejected" class="no-recording-section">
        <div class="no-recording-message">
          <Icon icon="mdi:lock-outline" width="18" height="18" />
          <span>Not authorized to view this call</span>
        </div>
      </div>

      <template v-else>
        <div v-if="recordingUrl || callData?.recordingUrl" class="recording-section">
          <div class="recording-section__header">
            <h6 class="section-title">Call Recording</h6>
            <span class="recording-download-wrapper">
              <button
                type="button"
                class="recording-download-btn"
                :disabled="isDownloading"
                @click.stop="downloadRecording"
              >
                <Icon
                  v-if="!isDownloading"
                  icon="material-symbols:download-rounded"
                  width="16"
                  height="16"
                />
                <LoadingBars v-else size="sm" :showText="false" />
              </button>
              <span class="recording-download-tooltip">Download recording</span>
            </span>
          </div>
          <div class="audio-player-wrapper" @click.stop>
            <AudioPlayer
              :recording-sid="recordingSid ?? callData?.recordingSid ?? undefined"
              :recording-url="recordingUrl ?? callData?.recordingUrl ?? undefined"
              :duration="callData?.duration"
              :compact="true"
            />
          </div>
        </div>

        <!-- Processing Recording Message -->
        <div
          v-else-if="callSid && (recordingSid || callData?.recordingSid) && activityLog?.callLog"
          class="no-recording-section"
        >
          <div class="no-recording-message">
            <Icon icon="mdi:information-outline" width="18" height="18" />
            <span>Processing recording...</span>
          </div>
        </div>

        <!-- Awaiting Recording -->
        <div v-else-if="callSid && awaitingRecording" class="no-recording-section">
          <div class="no-recording-message processing">
            <Icon icon="mdi:loading" width="18" height="18" class="spin-icon" />
            <span>Recording is being processed...</span>
          </div>
        </div>

        <!-- No Recording Available -->
        <div
          v-else-if="
            callSid &&
            !(recordingSid || callData?.recordingSid) &&
            (!activityLog?.callLog ||
              activityLog?.callLog?.status === 'completed' ||
              activityLog?.callLog?.status === 'failed')
          "
          class="no-recording-section"
        >
          <div class="no-recording-message">
            <Icon icon="mdi:information-outline" width="18" height="18" />
            <span>
              {{
                !activityLog?.callLog
                  ? "Call was initiated but not completed"
                  : "Recording is not available for this call"
              }}
            </span>
          </div>
        </div>

        <div v-if="isLoadingCallData" class="loading-state">
          <LoadingBars size="sm" />
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <p class="error-message">{{ error }}</p>
          <button type="button" class="btn btn-primary btn-sm" @click="() => refetch()">
            Retry
          </button>
        </div>

        <!-- Analysis Content -->
        <CallAnalysisContent
          v-else
          :call-data="callData"
          :is-loading="false"
          @regenerate="handleRegenerate"
          @send-follow-up-email="handleFollowUpEmail"
          @refresh-transcript="handleRefreshTranscript"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AudioPlayer, LoadingBars } from "@/components/ui";
import { useCallLogBySid } from "@/composables/calls";
import { useCallDispositions } from "@/composables/calls/useCallDispositions";
import { useLogCallLogDetails } from "@/composables/calls/useCallLog";
import { useToast } from "@/composables/ui/useToast";
import type { CallLogResponse } from "@/dtos/CallLog.dto";
import CallLogService from "@/services/call-log";
import { getSocket } from "@/utils/socket";
import { joinRoom, leaveRoom } from "@/utils/socket-rooms";
import { Icon } from "@iconify/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import CallAnalysisContent from "./CallAnalysisContent.vue";

const props = defineProps<{
  callSid: string | null;
  activityLog: { callLog?: any } | null;
  isExpanded: boolean;
  performedBy?: string | null;
  recordingSid: string | null;
  recordingUrl: string | null;
  callStatus: string | null;
  loggedDate: string;
  loggedTime: string;
}>();

const emit = defineEmits<{
  expand: [];
  collapse: [];
  "switch-channel": [payload: { channel: string; subject?: string; content?: string }];
}>();

const store = useStore();
const queryClient = useQueryClient();
const account = computed(
  () => store.getters.account.data as { organizationId?: string } | undefined
);
const organizationId = computed(() => account.value?.organizationId);

const callSidRef = computed(() => props.callSid);
const isExpandedAndReady = computed(
  () => !!props.isExpanded && !!props.callSid && !!account.value?.organizationId
);

const {
  data: queryData,
  isPending,
  isFetching,
  error: queryError,
  refetch,
} = useCallLogBySid(callSidRef, organizationId, { enabled: isExpandedAndReady });

const isLoadingCallData = computed(() => isPending.value && isFetching.value);
const isDownloading = ref(false);

// Disposition (for unanswered calls)
const $toast = useToast();
const { data: callDispositionsData } = useCallDispositions();
const callDispositions = computed(() => (callDispositionsData.value as any[]) || []);

const localDispositionId = ref<string | null>(null);
const localDispositionName = ref<string | null>(null);
const isUpdatingDisposition = ref(false);
const showDispositionDropdown = ref(false);
const dispositionAnchorRef = ref<HTMLElement | null>(null);
const dropdownStyle = ref<Record<string, string>>({});

const { mutateAsync: logCallLogDetails } = useLogCallLogDetails();

onMounted(() => {
  localDispositionId.value =
    (props.activityLog?.callLog?.callDispositionId as string | null) ?? null;
  localDispositionName.value =
    (props.activityLog?.callLog?.callDisposition?.name as string | null) ?? null;
});

// Falls back to list lookup when relation wasn't eager-loaded (fallback code path)
const displayDispositionName = computed(() => {
  if (localDispositionName.value) return localDispositionName.value;
  if (localDispositionId.value && callDispositions.value.length > 0) {
    return (
      (callDispositions.value.find((d: any) => d.id === localDispositionId.value) as any)?.name ??
      null
    );
  }
  return null;
});

const getRecordingDownloadUrl = () => {
  const token = store.getters.sessionToken;
  const sid = props.recordingSid ?? callData.value?.recordingSid;
  if (!token || !sid) return null;
  const base = `${
    process.env.NODE_ENV === "development"
      ? process.env.VUE_APP_LOCAL_SERVER_URL
      : process.env.VUE_APP_SERVER_URL
  }/${process.env.VUE_APP_VERSION}`;
  return `${base}/twilio/play-recording/${sid}?token=${encodeURIComponent(token)}`;
};

const downloadRecording = async () => {
  const url = getRecordingDownloadUrl();
  if (!url) return;
  isDownloading.value = true;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `call-recording-${props.callSid || "unknown"}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error("Failed to download recording:", err);
  } finally {
    isDownloading.value = false;
  }
};

const socketOverrideRef = ref<CallLogResponse | null>(null);
const parentCallSid = ref<string | null>(null);
const joinRejected = ref(false);

let socket: ReturnType<typeof getSocket> | null = null;
let listenersAttached = false;
let joinedCallRoom = false;
let reconnectHandler: (() => void) | null = null;

const unansweredStatuses = new Set(["no-answer", "busy", "failed", "rejected", "canceled"]);
const isUnansweredCall = computed(
  () => !!props.callStatus && unansweredStatuses.has(props.callStatus)
);
const unansweredTitle = computed(() => {
  const titleMap: Record<string, string> = {
    "no-answer": "Call — No Answer",
    busy: "Call — Busy",
    failed: "Call — Failed",
    rejected: "Call — Rejected",
    canceled: "Call — Canceled",
  };
  return props.callStatus
    ? titleMap[props.callStatus] || "Call — Not Connected"
    : "Call — Not Connected";
});

const callData = computed(
  () =>
    socketOverrideRef.value ??
    queryData.value ??
    (props.activityLog?.callLog as CallLogResponse | undefined) ??
    null
);

const error = computed(() => {
  if (queryError.value) {
    const e = queryError.value;
    return e instanceof Error ? e.message : "Failed to load call analysis. Please try again.";
  }
  if (!isLoadingCallData.value && !queryData.value && props.isExpanded && props.callSid) {
    return "No call data found for this call ID";
  }
  return null;
});

const awaitingRecording = computed(() => {
  const data = callData.value || props.activityLog?.callLog;
  if (!data) return false;
  if (data.recordingSid) return false;
  if (data.saveRecording === false) return false;
  if (data.status !== "completed") return false;
  if (!data.duration || data.duration <= 0) return false;
  return true;
});

function toggleDispositionDropdown() {
  if (showDispositionDropdown.value) {
    showDispositionDropdown.value = false;
    return;
  }
  if (dispositionAnchorRef.value) {
    const rect = dispositionAnchorRef.value.getBoundingClientRect();
    const showAbove = window.innerHeight - rect.bottom < 220;
    dropdownStyle.value = {
      position: "fixed",
      left: `${rect.left}px`,
      minWidth: `${Math.max(rect.width, 160)}px`,
      zIndex: "10070",
      ...(showAbove
        ? { bottom: `${window.innerHeight - rect.top + 4}px` }
        : { top: `${rect.bottom + 4}px` }),
    };
  }
  showDispositionDropdown.value = true;
}

function closeDispositionDropdown() {
  showDispositionDropdown.value = false;
}

async function selectDisposition(disp: { id: string; name: string }) {
  showDispositionDropdown.value = false;
  if (!props.callSid || localDispositionId.value === disp.id) return;
  isUpdatingDisposition.value = true;
  try {
    await logCallLogDetails({
      callSid: (props.activityLog?.callLog?.parentCallSid as string | undefined) || props.callSid,
      callDispositionId: disp.id,
    });
    localDispositionId.value = disp.id;
    localDispositionName.value = disp.name;
    queryClient.invalidateQueries({ queryKey: ["activityLogs"] });
    $toast.success("Disposition updated");
  } catch {
    $toast.error("Failed to update disposition");
  } finally {
    isUpdatingDisposition.value = false;
  }
}

function formatStatus(status: string | null) {
  if (!status) return "";
  return status
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getStatusClass(status: string) {
  const statusMap: Record<string, string> = {
    completed: "status-success",
    "no-answer": "status-warning",
    initiated: "status-secondary",
    rejected: "status-danger",
    busy: "status-warning",
    failed: "status-danger",
  };
  return statusMap[status] || "status-secondary";
}

const hasCallDataInActivityLog = computed(
  () =>
    props.activityLog?.callLog &&
    (props.activityLog.callLog.analysis ||
      props.activityLog.callLog.analysisStatus ||
      props.activityLog.callLog.transcriptUrl ||
      props.activityLog.callLog.analysisError)
);

function isProcessing(data: CallLogResponse | null | undefined) {
  if (!data) return false;
  const s = data.analysisStatus;
  const ts = data.transcriptStatus;
  return s === "pending" || s === "in_progress" || ts === "pending" || ts === "in_progress";
}

function joinCallRoom(targetCallSid: string) {
  if (!socket || !targetCallSid) return;
  if (joinedCallRoom) return;
  const current = callData.value;
  const parentSid =
    props.activityLog?.callLog?.parentCallSid ?? current?.parentCallSid ?? targetCallSid;
  const roomName = `call:${parentSid}`;
  joinRoom(socket, roomName, (ack) => {
    if (!ack.ok) joinRejected.value = true;
  });
  joinedCallRoom = true;
  parentCallSid.value = parentSid;
}

function leaveCallRoom() {
  if (!socket || !joinedCallRoom) return;
  const roomToLeave = parentCallSid.value ?? props.callSid;
  if (roomToLeave) leaveRoom(socket, `call:${roomToLeave}`);
  joinedCallRoom = false;
}

function setupSocketListeners(shouldJoinRoom = true) {
  if (listenersAttached || !props.callSid) return;
  try {
    socket = getSocket();
    if (!socket) return;

    const current = callData.value;
    const currentStatus = current?.analysisStatus ?? props.activityLog?.callLog?.analysisStatus;
    const currentTranscriptStatus =
      current?.transcriptStatus ?? props.activityLog?.callLog?.transcriptStatus;
    const processing = isProcessing(current ?? props.activityLog?.callLog);

    if (!processing && (currentStatus === "completed" || currentStatus === "failed")) return;

    listenersAttached = true;

    if (shouldJoinRoom && processing) {
      if (socket.connected && props.callSid) {
        joinCallRoom(props.callSid);
      } else {
        reconnectHandler = () => {
          if (props.callSid && processing) joinCallRoom(props.callSid);
        };
        socket.on("connect", reconnectHandler);
      }
    }

    socket.on("call:analysis:start", (data: any) => {
      const cur = callData.value;
      const callSidMatches =
        data.callSid === props.callSid ||
        data.callSid === parentCallSid.value ||
        data.callSid === cur?.callSid ||
        data.parentCallSid === props.callSid ||
        data.parentCallSid === parentCallSid.value ||
        data.parentCallSid === cur?.parentCallSid;

      if (callSidMatches) {
        if (data.parentCallSid) parentCallSid.value = data.parentCallSid;
        else if (data.callSid && !parentCallSid.value) parentCallSid.value = data.callSid;
        if (cur && cur.analysisStatus !== "completed") {
          socketOverrideRef.value = { ...cur, analysisStatus: "in_progress" };
        }
      }
    });

    socket.on("call:analysis:complete", (data: any) => {
      const cur = callData.value;
      const callSidMatches =
        data.callSid === props.callSid ||
        data.callSid === parentCallSid.value ||
        data.callSid === cur?.callSid ||
        data.parentCallSid === props.callSid ||
        data.parentCallSid === parentCallSid.value ||
        data.parentCallSid === cur?.parentCallSid;

      if (callSidMatches) {
        if (data.parentCallSid) parentCallSid.value = data.parentCallSid;
        else if (data.callSid && !parentCallSid.value) parentCallSid.value = data.callSid;

        const nextCallData: CallLogResponse = {
          ...(cur || ({} as CallLogResponse)),
          callSid: props.callSid || data.callSid,
          parentCallSid:
            data.parentCallSid || parentCallSid.value || cur?.parentCallSid || undefined,
          analysisStatus: "completed",
          analysis: {
            id: data.analysis?.id || "",
            callLogId: props.callSid || data.callSid,
            summary: data.analysis?.summary || "",
            actionItems: data.analysis?.actionItems ?? null,
            topics: data.analysis?.topics ?? null,
            sentiment: data.analysis?.sentiment ?? null,
            modelName: data.analysis?.modelName ?? null,
            callMetadata: data.analysis?.callMetadata ?? null,
            executiveSummary: data.analysis?.executiveSummary ?? null,
            detectiveAnalysis: data.analysis?.detectiveAnalysis ?? null,
            followUpStrategy: data.analysis?.followUpStrategy ?? null,
            emailRequestedInCall: data.analysis?.emailRequestedInCall ?? false,
            followUpEmail: data.analysis?.followUpEmail ?? null,
            createdAt: data.analysis?.createdAt || new Date(),
          },
          isLiveConversation: data.analysis?.isLiveConversation ?? cur?.isLiveConversation ?? false,
          isConnectedConversation:
            data.analysis?.isConnectedConversation ?? cur?.isConnectedConversation ?? false,
          connectReason: data.analysis?.connectReason ?? cur?.connectReason ?? null,
          transcriptUrl: data.transcriptUrl || cur?.transcriptUrl || null,
          recordingUrl: data.recordingUrl || cur?.recordingUrl || null,
        } as CallLogResponse;
        socketOverrideRef.value = nextCallData;
        queryClient.invalidateQueries({
          queryKey: ["callLog", props.callSid, organizationId.value],
        });
      }
    });

    socket.on("call:recording:available", (data: any) => {
      const cur = callData.value;
      const matches =
        data.callSid === props.callSid ||
        data.callSid === parentCallSid.value ||
        data.callSid === cur?.parentCallSid ||
        data.callSid === cur?.callSid;
      if (matches) {
        if (cur) {
          socketOverrideRef.value = {
            ...cur,
            recordingSid: data.recordingSid,
            recordingUrl: data.recordingUrl,
          };
        }
        queryClient.invalidateQueries({
          queryKey: ["callLog", props.callSid, organizationId.value],
        });
      }
    });
  } catch (err) {
    console.error("Failed to set up Socket.IO listeners for accordion:", err);
  }
}

function cleanupSocketListeners() {
  if (socket && listenersAttached) {
    try {
      socket.off("call:analysis:start");
      socket.off("call:analysis:complete");
      socket.off("call:recording:available");
      if (reconnectHandler) {
        socket.off("connect", reconnectHandler);
        reconnectHandler = null;
      }
      listenersAttached = false;
    } catch (err) {
      console.error("Error cleaning up accordion listeners:", err);
    }
  }
  leaveCallRoom();
}

function toggleAccordion() {
  if (!props.callSid) return;

  if (props.isExpanded) {
    emit("collapse");
    cleanupSocketListeners();
  } else {
    emit("expand");
    if (hasCallDataInActivityLog.value) {
      const status = props.activityLog!.callLog.analysisStatus;
      const transcriptStatus = props.activityLog!.callLog.transcriptStatus;
      if (
        transcriptStatus === "pending" ||
        transcriptStatus === "in_progress" ||
        status === "pending" ||
        status === "in_progress"
      ) {
        setupSocketListeners(true);
      } else if (status === "failed" || props.activityLog!.callLog.analysisError) {
        socketOverrideRef.value = null;
      }
    }
  }
}

watch(queryData, (newData) => {
  if (newData) socketOverrideRef.value = null;
});

watch(
  () => [props.recordingSid, props.recordingUrl],
  ([newSid, newUrl]) => {
    if (newSid && !newUrl && !callData.value?.recordingUrl) {
      refetch();
    }
  },
  { immediate: true }
);

watch(
  () => props.isExpanded,
  (newVal) => {
    if (!newVal) cleanupSocketListeners();
  }
);

watch(
  () => [queryData.value, props.isExpanded],
  ([data, expanded]) => {
    if (expanded && isProcessing(data as CallLogResponse | undefined)) {
      setupSocketListeners(true);
    }
  }
);

watch(showDispositionDropdown, (open) => {
  if (open) {
    document.addEventListener("click", closeDispositionDropdown);
  } else {
    document.removeEventListener("click", closeDispositionDropdown);
  }
});

function handleFollowUpEmail(payload: { subject: string; content: string }) {
  emit("switch-channel", { channel: "email", subject: payload.subject, content: payload.content });
}

async function handleRegenerate() {
  const cur = callData.value;
  if (!cur?.id) return;

  try {
    // Show in-progress state immediately
    socketOverrideRef.value = { ...(cur as CallLogResponse), analysisStatus: "in_progress" };
    // Reset listeners so setupSocketListeners can re-attach and join the call room
    cleanupSocketListeners();
    setupSocketListeners(true);
    await CallLogService.regenerateAnalysis(cur.id);
  } catch (err) {
    console.error("Failed to trigger regeneration:", err);
    socketOverrideRef.value = { ...(cur as CallLogResponse), analysisStatus: "failed" };
  }
}

// re-fetch CallLog so backend mints a fresh presigned transcriptUrl
function handleRefreshTranscript() {
  if (!props.callSid) return;
  queryClient.invalidateQueries({
    queryKey: ["callLog", props.callSid, organizationId.value],
  });
}

onBeforeUnmount(() => {
  cleanupSocketListeners();
  document.removeEventListener("click", closeDispositionDropdown);
});
</script>

<style scoped>
.call-analysis-accordion {
  width: 100%;
  margin-top: 8px;
  border: 1px solid #e4e4e7;
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.accordion-header {
  width: 100%;
  padding: 0;
  background: #fafafa;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-align: left;
}

.accordion-header:hover:not(:disabled) {
  background: #f5f5f5;
}

.accordion-header:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.call-analysis-accordion--flat {
  box-shadow: none;
}

.accordion-header--flat {
  cursor: default !important;
  opacity: 1 !important;
}

.accordion-header--flat:hover {
  background: #fafafa !important;
}

.phone-icon--muted {
  color: #9ca3af !important;
}

.accordion-header.is-expanded {
  background: #f8f9fa;
  border-bottom: 1px solid #e4e4e7;
}

.accordion-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  width: 100%;
  gap: 16px;
}

.accordion-left-section {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.phone-icon {
  color: #3e3efe;
  flex-shrink: 0;
}

.accordion-main-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.accordion-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.accordion-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2c2c2e;
  line-height: 1.4;
}

.accordion-subtitle {
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 500;
}

.accordion-right-section {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.accordion-status-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
}

.accordion-timestamp {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  text-align: right;
}

.timestamp-date {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.3;
}

.timestamp-time {
  font-size: 0.75rem;
  font-weight: 400;
  color: #9ca3af;
  line-height: 1.3;
}

.timestamp-performer {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 3px;
  font-size: 0.68rem;
  font-weight: 500;
  color: #6366f1;
  background: #eef2ff;
  border: 1px solid #e0e7ff;
  border-radius: 20px;
  padding: 1px 7px 1px 4px;
  white-space: nowrap;
  margin-top: 3px;
  line-height: 1.6;
}

.status-success {
  background-color: #dcfce7;
  color: #16a34a;
}

.status-warning {
  background-color: #fef3c7;
  color: #92400e;
}

.status-secondary {
  background-color: #f3f4f6;
  color: #6b7280;
}

.status-danger {
  background-color: #fee2e2;
  color: #dc2626;
}

.accordion-icon {
  flex-shrink: 0;
  color: #6b7280;
  transition: transform 0.2s ease-in-out;
}

.accordion-header.is-expanded .accordion-icon {
  transform: rotate(180deg);
}

.accordion-content {
  padding: 20px;
  background: #ffffff;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 2000px;
  }
}

.recording-section {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e4e4e7;
}

.recording-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.recording-section .section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #2c2c2e;
  margin: 0;
}

.recording-download-wrapper {
  position: relative;
  display: inline-flex;
}

.recording-download-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #e4e4e7;
  border-radius: 6px;
  background: #fff;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  transition: all 0.15s ease;
}

.recording-download-btn:hover:not(:disabled) {
  background: #f1f5f9;
  color: var(--color-primary, #5b5fc7);
  border-color: var(--color-primary, #5b5fc7);
}

.recording-download-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.recording-download-tooltip {
  display: none;
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #1e293b;
  color: #fff;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 9999;
}

.recording-download-wrapper:hover .recording-download-tooltip {
  display: block;
}

.audio-player-wrapper {
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
}

.recording-section :deep(.audio-player) {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.recording-section :deep(.audio-player__container) {
  border: none;
  background: transparent;
  padding: 0;
}

.no-recording-section {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e4e4e7;
}

.no-recording-message.processing {
  color: #b8860b;
}

.spin-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.no-recording-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  color: #92400e;
  font-size: 0.85rem;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 40px 20px;
}

.error-message {
  color: #dc2626;
  margin-bottom: 16px;
  font-size: 0.85rem;
}

.btn {
  padding: 6px 14px;
  border-radius: 7px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.2s ease-in-out;
}

.btn-primary {
  background-color: #3e3efe;
  border-color: #3e3efe;
  color: #ffffff;
}

.btn-primary:hover {
  background-color: #3230d6;
  border-color: #3230d6;
}

.btn-sm {
  padding: 4px 12px;
  font-size: 0.7rem;
}

/* Disposition chip — shown on all call rows */
.disp-chip-wrapper {
  position: relative;
  display: inline-flex;
}

.disp-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px 3px 8px;
  border-radius: 20px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.disp-chip:hover:not(.disp-chip--disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #374151;
}

.disp-chip--set {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1d4ed8;
}

.disp-chip--set:hover:not(.disp-chip--disabled) {
  background: #dbeafe;
  border-color: #93c5fd;
}

.disp-chip--disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.disp-dropdown {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 0;
}

.disp-dropdown__item {
  display: block;
  width: 100%;
  padding: 8px 14px;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.82rem;
  color: #374151;
  transition: background 0.1s;
  white-space: nowrap;
}

.disp-dropdown__item:hover {
  background: #f3f4f6;
}

.disp-dropdown__item--active {
  color: #2563eb;
  font-weight: 600;
  background: #eff6ff;
}
</style>
